import { CustomEpic, PayloadAction } from 'types/types';
import {
  BookAppointmentActionPayload,
  bookAppointmentFailure,
  bookAppointmentSuccess,
} from 'modules/appointments/actions';
import { ofType } from 'redux-observable';
import { BOOK_APPOINTMENT } from 'modules/appointments/actionTypes';
import { map, switchMap, tap } from 'rxjs/operators';
import { APPOINTMENTS_ENDPOINT } from 'modules/appointments/constants';
import { getId } from 'selectors/user/getId';
import { getAuthHeaders } from 'api/getAuthHeaders';
import { handleAjaxSuccess } from 'utils/epics/handleAjaxSuccess';
import { handleAjaxError } from 'utils/epics/handleAjaxError';
import { bookAppointmentCallTranslator } from 'modules/appointments/translator';
import { Observable, of } from 'rxjs';
import { fetchPatientCases } from 'modules/cases/actions';
import { fetchPatientCases$ } from 'modules/cases/epics/fetchPatientCases$';
import { getCaseForPatient } from 'selectors/cases/getCaseForPatient';
import { FETCH_CASES_SUCCESS } from 'modules/cases/actionTypes';
import toast from 'react-hot-toast';
import { getCurrentConnectionStatus } from 'utils/connectionStatus';
import { handleOfflineBooking } from 'modules/offlineSync/utils/handleOfflineBooking';
import { getIdempotencyHeaders } from 'utils/api/getIdempotencyHeaders';
import { reportBookingRefusal } from 'utils/logging/reportBookingRefusal';
import { AxiosError } from 'axios';
import { randomUUID } from 'utils/crypto/randomUUID';

export const bookAppointment$: CustomEpic<BookAppointmentActionPayload> = (
  action$,
  state$,
  { postApi$, getApi$ },
) =>
  action$.pipe(
    ofType(BOOK_APPOINTMENT),
    switchMap((action: PayloadAction<BookAppointmentActionPayload>) =>
      of(action),
    ),
    // Fetch cases for patient if none can be found
    switchMap(action => {
      const { patientId, caseId } = action.payload;
      if (caseId) {
        return of(action);
      }
      const patientCase = getCaseForPatient(state$.value, { patientId });
      if (!patientCase) {
        return fetchPatientCases$(
          of(
            fetchPatientCases({
              patientId: String(patientId),
            }),
          ) as Observable<any>,
          state$,
          { getApi$, postApi$ },
        ).pipe(
          map(result => {
            const payload = result.payload as Record<string, unknown>;
            const [caseId] = Object.keys(payload);
            if (result.type === FETCH_CASES_SUCCESS && caseId) {
              // Update payload with correct case id
              action.payload.caseId = Number(caseId);
            }
            return action; // pass hopefully updated payload to next item in chain
          }),
        );
      }
      return of(action);
    }),
    switchMap(action => {
      const {
        dateTime,
        type,
        therapistId,
        patientId,
        duration,
        caseId,
        recurrence,
        recursUntil,
      } = action.payload;
      const userId = getId(state$.value);

      const patientCase = getCaseForPatient(state$.value, { patientId });
      if (!caseId && !patientCase) {
        toast.error('Could not find case ID matching patient');
        return of(
          bookAppointmentFailure({
            name: 'Error',
            message: 'Could not find case ID matching patient',
          }),
        );
      }

      // Offline guard: create temp appointments and enqueue for later sync
      if (getCurrentConnectionStatus() === 'offline') {
        return handleOfflineBooking(action.payload, state$);
      }

      return postApi$({
        endpoint: APPOINTMENTS_ENDPOINT,
        payload: {
          app_datetime: dateTime,
          app_type: type,
          case_id: caseId || patientCase.id,
          duration,
          patient_id: patientId,
          therapist_id: therapistId || userId,
          user_id: userId,
          recurrence,
          recurs_until: recursUntil,
        },
        // Minted per request here, because this path has no queue item to
        //   carry a key. It still covers the retry that matters at this layer:
        //   the response interceptor replays the same config after refreshing
        //   an expired token, and that replay reuses this header rather than
        //   asking for a second appointment.
        //
        // A therapist tapping Book again after an error does produce a new key,
        //   and deliberately so — the app cannot tell a repeat of one intent
        //   from a second intent.
        //
        // **That case is not covered, and it is the shape of the 2026-08-11
        //   incident rather than a hypothetical.** All ten attempts against
        //   case 9223 are tabulated in `utils/appointment/toSlotDateTime`. The
        //   line that matters here: `08:30:00` was created at 15:25:00 and
        //   refused at 15:25:29 — the same payload, 29 seconds apart, opposite
        //   outcomes, and the whole run splits cleanly by clock rather than by
        //   payload.
        //
        // So Clinicient's answer is not a function of what we send, which rules
        //   out every payload-keyed account of it — including the guess this
        //   comment used to end on, that our zeroing is "probably what lets its
        //   check fire at all". No replacement theory is offered; three have
        //   died.
        //
        // What that leaves is the part with a consequence: idempotency cannot be
        //   delegated to a check whose outcome we cannot predict, and on this
        //   path it is not ours either. A fresh `randomUUID()` per request makes
        //   two taps two intents. Rows 1 to 5 are five requests 15 to 26 seconds
        //   apart that each created an appointment, which is that gap being
        //   exercised.
        //
        // Nothing on the form side closes it either. `Booking.tsx` used to pass
        //   `debounce(submitBooking, 500)`, which was removed rather than
        //   fixed: it was built inside the component body, so a render between
        //   two taps yielded a fresh debouncer and both fired, and 500 ms was
        //   never going to reach a 15-second gap even if it had been stable.
        //   What does hold is `BookingFormPage`'s `isSubmitting` latch, and it
        //   only covers one screen's lifetime — not a reload, and not a second
        //   device.
        //
        // The fix is to mint the key where the intent is captured and hold it
        //   across attempts, derived from the payload
        //   (`patientId` + `dateTime` + `type` + `duration` + `caseId`) so it
        //   needs no state and survives a reload. It goes in `idempotency_key`
        //   against the backend's `(idempotency_key, case_id, app_datetime)`
        //   index with no schema change — and the instant in it must be the
        //   normalised one, or two taps carrying different seconds derive
        //   different keys and miss our own dedupe.
        //
        // Deliberately not built yet: that table is blocked on a DDL decision,
        //   so a stable key would have nothing to dedupe against. Building ahead
        //   of a blocked dependency is the mistake this pair has already agreed
        //   not to repeat.
        //
        // `reportBookingRefusal` records the status of every refusal on this
        //   path, so what arrives here becomes data rather than argument.
        headers: {
          ...getAuthHeaders(state$.value),
          ...getIdempotencyHeaders(randomUUID()),
        },
      }).pipe(
        handleAjaxSuccess(
          bookAppointmentSuccess,
          bookAppointmentCallTranslator,
        ),
        // Observes the refusal and lets it through untouched. `handleAjaxError`
        //   already puts the status into `bookAppointmentFailure`, but redux is
        //   where it stops — nothing reads it and nothing ships it, so a
        //   refusal on this path leaves no trace anyone can count.
        tap({
          error: (err: AxiosError) =>
            reportBookingRefusal({
              error: err,
              path: 'online',
            }),
        }),
        handleAjaxError(bookAppointmentFailure),
      );
    }),
  );
